import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { 
  Printer, 
  Plus, 
  Settings, 
  Trash2, 
  Wifi, 
  Usb, 
  FileText,
  Image,
  Download,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';

interface PrinterDevice {
  id: string;
  name: string;
  type: 'inkjet' | 'laser' | 'thermal';
  connection: 'usb' | 'wifi' | 'bluetooth';
  status: 'online' | 'offline' | 'printing' | 'error';
  isDefault: boolean;
}

export const PrinterSettings: React.FC = () => {
  const [printers, setPrinters] = useState<PrinterDevice[]>([
    {
      id: '1',
      name: 'HP LaserJet Pro M404n',
      type: 'laser',
      connection: 'wifi',
      status: 'online',
      isDefault: true
    },
    {
      id: '2',
      name: 'Canon PIXMA TS3320',
      type: 'inkjet',
      connection: 'usb',
      status: 'offline',
      isDefault: false
    }
  ]);

  const [defaultPaper, setDefaultPaper] = useState('a4');
  const [defaultQuality, setDefaultQuality] = useState('normal');
  const [autoColorDetection, setAutoColorDetection] = useState(true);
  const [doubleSlided, setDoubleSided] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="text-green-500" size={16} />;
      case 'printing': return <Clock className="text-blue-500" size={16} />;
      case 'error': return <AlertCircle className="text-red-500" size={16} />;
      default: return <AlertCircle className="text-gray-400" size={16} />;
    }
  };

  const getConnectionIcon = (connection: string) => {
    switch (connection) {
      case 'wifi': return <Wifi size={16} className="text-primary" />;
      case 'usb': return <Usb size={16} className="text-primary" />;
      default: return <Settings size={16} className="text-primary" />;
    }
  };

  const setDefaultPrinter = (printerId: string) => {
    setPrinters(prev => prev.map(p => ({
      ...p,
      isDefault: p.id === printerId
    })));
  };

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Printer Settings</h1>
          <p className="text-muted-foreground">
            Manage printers and printing preferences
          </p>
        </div>

        {/* Installed Printers */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Printer size={20} />
                  Installed Printers
                </CardTitle>
                <CardDescription>
                  Manage your connected printers
                </CardDescription>
              </div>
              <Button size="sm" className="flex items-center gap-2">
                <Plus size={16} />
                Add Printer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {printers.map((printer) => (
              <div key={printer.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center gap-1">
                    <Printer size={24} className="text-primary" />
                    {getConnectionIcon(printer.connection)}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{printer.name}</h3>
                      {printer.isDefault && (
                        <Badge variant="secondary" className="text-xs">Default</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {getStatusIcon(printer.status)}
                      <span className="capitalize">{printer.status}</span>
                      <span>•</span>
                      <span className="capitalize">{printer.type} printer</span>
                      <span>•</span>
                      <span className="capitalize">{printer.connection}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!printer.isDefault && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setDefaultPrinter(printer.id)}
                    >
                      Set as Default
                    </Button>
                  )}
                  <Button variant="outline" size="sm">
                    <Settings size={16} />
                  </Button>
                  <Button variant="outline" size="sm" className="text-destructive">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Default Print Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText size={20} />
              Default Print Settings
            </CardTitle>
            <CardDescription>
              Set default preferences for all print jobs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paper-size">Paper Size</Label>
                <Select value={defaultPaper} onValueChange={setDefaultPaper}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select paper size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4 (210 x 297 mm)</SelectItem>
                    <SelectItem value="letter">Letter (8.5 x 11 in)</SelectItem>
                    <SelectItem value="legal">Legal (8.5 x 14 in)</SelectItem>
                    <SelectItem value="a3">A3 (297 x 420 mm)</SelectItem>
                    <SelectItem value="a5">A5 (148 x 210 mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="print-quality">Print Quality</Label>
                <Select value={defaultQuality} onValueChange={setDefaultQuality}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select quality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (Fast)</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High Quality</SelectItem>
                    <SelectItem value="photo">Photo Quality</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="auto-color">Automatic Color Detection</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically detect and use color when needed
                  </p>
                </div>
                <Switch
                  id="auto-color"
                  checked={autoColorDetection}
                  onCheckedChange={setAutoColorDetection}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="double-sided">Double-Sided Printing</Label>
                  <p className="text-sm text-muted-foreground">
                    Print on both sides of the paper by default
                  </p>
                </div>
                <Switch
                  id="double-sided"
                  checked={doubleSlided}
                  onCheckedChange={setDoubleSided}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Print Queue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock size={20} />
              Print Queue
            </CardTitle>
            <CardDescription>
              Current and recent print jobs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No print jobs in queue</p>
              <p className="text-sm">Documents will appear here when printing</p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};